import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { get } from 'lodash';
import { BooleanEnum, KnownSearchParams, VolkswagenLabels } from '../../resources/base/models/enums';
import { FuelType } from '../../resources/base/models/vehicleTypes';
import { DebugLog, DeepPartial, OptionsItem, OptionsListToEnum } from './miscUtils.types';

/**
 * - Helper for picking value from objects designed to for `select`-like form elements.
 * - It's role is to allow easy and type-safe extraction of value from such objects.
 */
export const pickOptionsValue = <T extends Readonly<OptionsItem[]>, V extends T[number]['value']>(
  optionsObject: T,
  valueToPick: V
) => {
  const itemFound = optionsObject.find((i) => i.value === valueToPick)?.value;

  if (itemFound === null) {
    throw new Error(
      `Value ${valueToPick} could not be found in ${optionsObject}. If typings are correct it should never occur.`
    );
  }

  return itemFound;
};

export const isEngineElectricOrHybrid = (engine: FuelType) => [FuelType.Hybrid, FuelType.Electric].includes(engine);
export const isEngineElectric = (engine: FuelType) => [FuelType.Electric].includes(engine);

export const useScrollToFormError = (
  isValidating: boolean,
  isSubmitting: boolean,
  errors: Record<any, any>,
  errorClasses: string[] = ['is-error']
) => {
  useEffect(() => {
    const errorElem = errorClasses
      .map((errorClass) => document.querySelector(`.${errorClass}`) ?? null)
      .filter(Boolean)[0];
    if (
      errors !== undefined &&
      Object.entries(errors).length !== 0 &&
      errors.constructor === Object &&
      !isValidating &&
      !isSubmitting &&
      errorElem
    ) {
      errorElem.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValidating, isSubmitting, errors]);
};

/**
 * - Crawls object and removes values deemed as 'empty'.
 * - Empty values for now are: null, '', undefined, arrays and objects of length 0.
 * - Mutates source object.
 * @param emptyValuesDefinition - If certain fields values different than null, '' and undefined
 * should also be considered empty you can specify this with this object.
 * @param path - Internal holder of path to currently checked field. Do not use.
 */
export const removeEmptyFromObject = <T extends Record<string, any>>(
  obj: T,
  emptyValuesDefinition?: DeepPartial<T>,
  allowNull = false,
  path?: string[]
) => {
  if (emptyValuesDefinition) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    path = path ?? [];
  }

  Object.entries(obj).forEach(([key, val]) => {
    let subPath = [] as string[];

    if (emptyValuesDefinition && path) {
      subPath = [...path, key];
    }

    // prettier-ignore
    const isNonEmptyObject =
      val
      && typeof val === 'object'
      && Object.keys(removeEmptyFromObject(val, emptyValuesDefinition, allowNull, subPath)).length !== 0;

    if (isNonEmptyObject) return;

    // prettier-ignore
    const isEmptyValueOrObject =
      (!allowNull && val === null)
      || val === ''
      || val === undefined
      || val === get(emptyValuesDefinition, subPath)
      || (!allowNull && typeof val === 'object' && Object.keys(val).length === 0);

    if (isEmptyValueOrObject) delete obj[key];
  });

  return obj;
};

/**
 * - Objects representing TS enums can have some extra keys for fields with numeric values,
 * so we filter them out.
 */
export const getEnumKeys = (optionsEnum: Record<any, any>) => {
  return Object.keys(optionsEnum).filter((key) => Number.isNaN(Number(key)));
};

export const getEnumValues = <E extends Record<any, any>>(optionsEnum: E): Array<E[keyof E]> => {
  return getEnumKeys(optionsEnum).map((key) => optionsEnum[key]);
};

export const getOptionsListValues = <OL extends Array<{ value: string; label: string }>>(optionsList: OL) => {
  return optionsList.map(({ value }) => (value === '' ? null : value)).filter(Boolean) as Array<
    Exclude<OL[number]['value'], ''>
  >;
};

export const getOptionsListEnum = <OL extends ReadonlyArray<{ value: string; label: string }>>(optionsList: OL) => {
  return optionsList.reduce((newEnum, { value }) => ({ ...newEnum, [value]: value }), {} as OptionsListToEnum<OL>);
};

export const getLabelFromOptionsLit = <OL extends ReadonlyArray<{ value: string; label: string }>>(
  optionsList: OL,
  value: OL[number]['value']
): Extract<OL[number], { value: typeof value }>['label'] | undefined => {
  return optionsList.find(({ value: _value }) => _value === value)?.label;
};

export const getIsSoleTrader = (legalEntity: QuoteSchema.Schemas.EntityLegalFormType | undefined) =>
  legalEntity === 'EI' || legalEntity === 'INDIV_PP';

export const boolToStrBool = (bool: boolean) => {
  if (bool) return BooleanEnum.true;
  return BooleanEnum.false;
};

export const strBoolToBool = <T extends BooleanEnum>(strBool: T) => {
  if (strBool === 'true') return true;
  return false;
};

export const getDisplayedManufacturerLabel = (
  manufacturer: VehicleIntegration.Components.Schemas.ManufacturerType
): VehicleIntegration.Components.Schemas.ManufacturerType | VolkswagenLabels => {
  return manufacturer === 'Volkswagen Nutzfahrzeuge' ? VolkswagenLabels.VW_LCV : manufacturer;
};

const getExternalIdentifierIdFromVehicle = <
  T extends { externalVehicleIdentifiers?: Array<VehicleIntegration.Components.Schemas.ExternalIdentifier> },
>(
  data: T | null,
  type: VehicleIntegration.Components.Schemas.IdentifierSourceType
): string | null => {
  if (!data?.externalVehicleIdentifiers || !Array.isArray(data.externalVehicleIdentifiers)) {
    return null;
  }

  return data.externalVehicleIdentifiers.find((identifier) => identifier.type === type)?.id ?? null;
};

export const getMilesCatalogVehicleIdFromVehicle = <
  T extends { externalVehicleIdentifiers?: Array<VehicleIntegration.Components.Schemas.ExternalIdentifier> },
>(
  data: T | null
): string | null => {
  return getExternalIdentifierIdFromVehicle(data, 'MILES_CATALOG_VEHICLE_ID');
};

export const getJatoReference3FromVehicle = <
  T extends { externalVehicleIdentifiers?: Array<VehicleIntegration.Components.Schemas.ExternalIdentifier> },
>(
  data: T | null
): string | null => {
  return getExternalIdentifierIdFromVehicle(data, 'JATO_REFERENCE_3');
};

/**
 * - Use where concretizing given value type to not-undefined
 * is in line with required business logic.
 * - Does not return a value and makes TS assume that assertion will interrupt
 * parent function if value is not defined.
 */
export function /* assertion cannot be arrow fn */ assertDefinedVoid<T>(
  value: T | undefined,
  errorMessage?: string
): asserts value is T {
  if (value === undefined) {
    throw new Error('Asserted valued was undefined. ' + (errorMessage ?? ''));
  }
}

/**
 * - Use where concretizing given value type to not-undefined
 * is in line with required business logic.
 */
export const assertDefined = <T>(value: T | undefined, errorMessage?: string): T => {
  if (value === undefined) {
    throw new Error('Asserted valued was undefined. ' + (errorMessage ?? ''));
  }

  return value;
};

export const deduplicateStringsArray = <T extends string>(ar: T[]): T[] => {
  return ar.sort().filter((value, index, _ar) => {
    return _ar[index - 1] !== value;
  });
};

export const sortObjectsArray = <T extends Record<string, any>>(ar: T[], getObjectValue: (o: T) => any): T[] => {
  return ar.sort((a, b) => getObjectValue(a) - getObjectValue(b));
};

export const deduplicateObjectsArray = <T extends Record<string, any>>(
  ar: T[],
  getObjectValue: (o?: T) => any
): T[] => {
  return sortObjectsArray(ar, getObjectValue).filter((o, index, _ar) => {
    return getObjectValue(_ar[index - 1]) !== getObjectValue(o);
  });
};

export const isEnv = (...envsToCheckFor: Array<'dev' | 'int' | 'cons' | 'prod'>) => {
  return envsToCheckFor.includes(process.env.REACT_APP_STAGE || ('' as any));
};

export const useStepFromUrl = () => {
  const { step } = useParams();
  return typeof Number(step) === 'number' ? Number(step) : null;
};

export const removeSearchParamsFromUrl = (...paramsToRemove: KnownSearchParams[]) => {
  const url = new URL(window.location.href);
  paramsToRemove.forEach((param) => url.searchParams.delete(param));
  window.history.replaceState(null, '', url.toString());
};

export const waitForMiles = () => {
  const timeout = isEnv('prod') ? 5000 : 2000;

  return new Promise((resolve) => {
    setTimeout(() => resolve(true), timeout);
  });
};

export const isNotesEnabled = () => {
  return true;
};

export const initOptional = <T>(initialValue?: T) => {
  return initialValue ?? (undefined as T | undefined);
};

export const debugLog = ({ message, level = 'LOG' }: DebugLog) => {
  if (isEnv('prod')) {
    return;
  }

  switch (level) {
    case 'ERROR':
      // eslint-disable-next-line no-console
      console.error(message);
      break;
    case 'LOG':
    default:
      // eslint-disable-next-line no-console
      console.log(message);
      break;
  }
};

export const arrayToEnum = <T extends ReadonlyArray<any>>(a: T) => {
  return a.reduce((acc, v) => ({ ...acc, [v]: v }), {}) as { [k in T[number]]: k };
};
